/**
 * GDriveFoldersSection
 *
 * "Shared folders" management block for a single GDrive channel, embedded in
 * ChannelSettings.tsx (SPEC §5.3, §7; ACCESS-MODE extension).
 *
 * Renders one of three states, branched strictly on `data.mode` (never on
 * `folders.length` — `mode: 'all'` also has an empty `folders` array):
 *   - `'unconfigured'`: fail-closed notice. If `canEdit`, offers BOTH
 *     "Configure shared folders" (→ scoped) and "Share entire Drive" (→ all).
 *     Otherwise, a "waiting for the connector" note.
 *   - `'scoped'`: the existing folder-chip list + "Add", PLUS (if `canEdit`)
 *     a "Share entire Drive instead" action that broadens access — gated
 *     behind an inline confirm since it widens what the team can import.
 *   - `'all'`: a "whole Drive" notice. If `canEdit`, offers "Restrict to
 *     specific folders" (→ scoped, via the same folder Picker as "Add").
 *
 * Add/remove/mode-switch race safety (code-review finding): a SINGLE
 * `useUpdateGDriveFolders()` instance is created here and passed into
 * `useGDriveFolderPicker` (the "Add" / "Restrict" / "Share entire Drive"
 * flows) so every mutating action shares one `isPending` flag. All trigger
 * buttons are disabled while that flag is true, so no two PUTs computed from
 * a stale read can ever race each other.
 *
 * Positioning-bug postmortem (2026-07-31): the "Add"/"Configure"/"Restrict"
 * buttons call `openPicker()` DIRECTLY — no wrapping panel/modal/overlay of
 * any kind. Any competing full-viewport UI mounted while the Google Picker
 * opens (even a plain non-Radix backdrop, not just Radix Dialog) throws off
 * the Picker's own viewport-fixed positioning — see `useGDriveFolderPicker.ts`
 * and the "Google Picker — viewport-fixed positioning overrides" block in
 * `index.css`. The nudge/warning/leaf-folder copy that used to live in a
 * separate confirm step is now all inline in this section instead. The
 * scoped→all confirm below is a plain inline block for the same reason.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/ui/Icon';
import { Tooltip } from '@/components/ui/Tooltip';
import { ICONS } from '@/constants/icons';
import { useGDriveFolders, useUpdateGDriveFolders } from '@/hooks/useGDriveImport';
import { useGDriveFolderPicker } from '@/hooks/useGDriveFolderPicker';
import { toast } from '@/utils/toast';
import { cn } from '@/utils/cn';

interface GDriveFoldersSectionProps {
  channelId: number;
  className?: string;
}

export const GDriveFoldersSection = ({ channelId, className = '' }: GDriveFoldersSectionProps) => {
  const { t } = useTranslation('settings');
  const { data, isLoading } = useGDriveFolders(channelId);
  const updateFolders = useUpdateGDriveFolders();
  const [confirmingShareAll, setConfirmingShareAll] = useState(false);

  const folders = data?.folders ?? [];
  const { openPicker, shareEntireDrive, pickerError } = useGDriveFolderPicker({
    channelId,
    existingFolders: folders,
    mutation: updateFolders,
    onSaved: () => setConfirmingShareAll(false),
  });

  if (isLoading) {
    return (
      <div className={cn('text-xs text-gray-400', className)}>
        {t('gdrive_folders_loading', { defaultValue: 'Loading shared folders…' })}
      </div>
    );
  }

  if (!data) return null;

  const { canEdit, mode } = data;

  const handleRemove = (folderId: string) => {
    if (folders.length <= 1) return; // guarded below too — at least 1 required
    const next = folders.filter((f) => f.id !== folderId);
    updateFolders.mutate(
      { channelId, mode: 'scoped', folders: next },
      {
        onError: () => {
          toast.error(
            t('gdrive_folders_save_error', { defaultValue: 'Failed to save shared folders. Please try again.' }),
          );
        },
      },
    );
  };

  const handleConfirmShareAll = () => {
    shareEntireDrive();
  };

  return (
    <div className={cn('rounded-lg border border-gray-100 bg-gray-50/60 p-3', className)}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <Icon
            name={mode === 'all' ? ICONS.GLOBE : ICONS.FOLDER}
            size="sm"
            color="text-gray-500"
            ariaHidden
          />
          <span className="text-xs font-semibold text-gray-700">
            {t('gdrive_folders_section_title', { defaultValue: 'Google Drive access' })}
          </span>
          {mode === 'all' && (
            <>
              <span className="text-gray-300" aria-hidden>·</span>
              <span className="text-xs font-medium text-dark-teal">
                {t('gdrive_folders_mode_all_title', { defaultValue: 'Entire Drive' })}
              </span>
            </>
          )}
        </div>
        {canEdit && mode === 'scoped' && (
          <Tooltip
            content={t('gdrive_folder_step_leaf_guidance', {
              defaultValue:
                'Pick the folders that directly contain your files (e.g. Bilder, Videos, Blog) — not an umbrella parent. Files inside sub-folders aren\'t shared unless you add that sub-folder too.',
            })}
          >
            <button
              onClick={openPicker}
              disabled={updateFolders.isPending}
              className="text-xs font-medium text-primary-600 hover:text-primary-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <Icon name={ICONS.PLUS} size="xs" ariaHidden className="mr-1 inline-block" />
              {t('gdrive_folders_add_action', { defaultValue: 'Add' })}
            </button>
          </Tooltip>
        )}
      </div>

      {mode === 'unconfigured' ? (
        canEdit ? (
          <div className="mt-2 flex items-start gap-2">
            <Icon name={ICONS.WARNING} size="xs" color="text-amber-500" ariaHidden className="mt-0.5 flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-xs text-amber-700">
                {t('gdrive_folders_unconfigured_editable', {
                  defaultValue: 'No folders shared yet — team members cannot import files until you choose some.',
                })}
              </p>
              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                <button
                  onClick={openPicker}
                  disabled={updateFolders.isPending}
                  className="text-xs font-semibold text-primary-600 hover:text-primary-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  {t('gdrive_folders_configure_action', { defaultValue: 'Configure shared folders' })}
                </button>
                <button
                  onClick={shareEntireDrive}
                  disabled={updateFolders.isPending}
                  className="text-xs font-semibold text-gray-600 hover:text-gray-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  {t('gdrive_folders_unconfigured_all_action', { defaultValue: 'Share entire Drive' })}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <p className="mt-2 text-xs text-gray-500">
            {t('gdrive_folders_unconfigured_readonly', {
              defaultValue: 'Waiting for the teammate who connected this account to choose shared folders.',
            })}
          </p>
        )
      ) : mode === 'all' ? (
        <div className="mt-2">
          <p className="text-xs text-gray-500">
            {t('gdrive_folders_mode_all_description', {
              defaultValue: 'Your team can import any file.',
            })}
          </p>
          {canEdit && (
            <button
              onClick={openPicker}
              disabled={updateFolders.isPending}
              className="mt-1 text-xs font-semibold text-primary-600 hover:text-primary-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              {t('gdrive_folders_restrict_action', { defaultValue: 'Restrict to specific folders' })}
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {folders.map((folder) => (
              <span
                key={folder.id}
                className="inline-flex items-center gap-1 rounded-full bg-white border border-gray-200 px-2.5 py-1 text-xs text-gray-700"
              >
                <Icon name={ICONS.FOLDER} size="xs" color="text-gray-400" ariaHidden />
                <span className="max-w-[10rem] truncate">{folder.name || folder.id}</span>
                {canEdit && (
                  <Tooltip
                    content={
                      folders.length <= 1
                        ? t('gdrive_folders_remove_last_tooltip', {
                            defaultValue: 'At least one shared folder is required.',
                          })
                        : t('gdrive_folders_remove_action', { defaultValue: 'Remove' })
                    }
                  >
                    <button
                      onClick={() => handleRemove(folder.id)}
                      disabled={folders.length <= 1 || updateFolders.isPending}
                      className="ml-0.5 text-gray-400 hover:text-red-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      aria-label={t('gdrive_folders_remove_action', { defaultValue: 'Remove' })}
                    >
                      <Icon name={ICONS.CROSS} size="xs" ariaHidden />
                    </button>
                  </Tooltip>
                )}
              </span>
            ))}
          </div>
          {!canEdit && (
            <p className="mt-2 text-xs text-gray-400">
              {t('gdrive_folders_readonly_note', {
                defaultValue: 'Only the teammate who connected this account (or an admin) can change this.',
              })}
            </p>
          )}
          {/* Persistent leaf-folder reminder — only files directly inside a
              shared folder are visible, sub-folders need to be added too. */}
          <p className="mt-2 text-xs text-gray-400">
            {t('gdrive_folders_leaf_note', {
              defaultValue: 'Only files directly inside these folders are shared. Add each sub-folder you want included.',
            })}
          </p>

          {canEdit && (
            <div className="mt-2 border-t border-gray-200 pt-2">
              {confirmingShareAll ? (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                  <div className="flex items-start gap-2">
                    <Icon name={ICONS.WARNING} size="xs" color="text-amber-600" ariaHidden className="mt-0.5 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-amber-900">
                        {t('gdrive_folders_share_all_confirm_title', { defaultValue: 'Share your entire Drive?' })}
                      </p>
                      <p className="mt-0.5 text-xs text-amber-800">
                        {t('gdrive_folders_share_all_confirm_text', {
                          defaultValue: 'Your team will be able to import any file from this Google Drive.',
                        })}
                      </p>
                      <div className="mt-2 flex items-center gap-2">
                        <button
                          onClick={handleConfirmShareAll}
                          disabled={updateFolders.isPending}
                          className="rounded-md bg-primary-600 px-2.5 py-1 text-xs font-semibold text-primary-600-contrast hover:bg-primary-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        >
                          {t('gdrive_folders_share_all_confirm_button', { defaultValue: 'Share entire Drive' })}
                        </button>
                        <button
                          onClick={() => setConfirmingShareAll(false)}
                          disabled={updateFolders.isPending}
                          className="text-xs font-medium text-gray-500 hover:text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        >
                          {t('gdrive_folders_share_all_cancel_button', { defaultValue: 'Cancel' })}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setConfirmingShareAll(true)}
                  disabled={updateFolders.isPending}
                  className="text-xs font-medium text-gray-500 hover:text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <Icon name={ICONS.GLOBE} size="xs" ariaHidden className="mr-1 inline-block" />
                  {t('gdrive_folders_share_all_action', { defaultValue: 'Share entire Drive instead' })}
                </button>
              )}
            </div>
          )}
        </>
      )}

      {pickerError && (
        <p className="mt-2 text-xs text-red-600">{pickerError.message}</p>
      )}
    </div>
  );
};
